import {FATCA_CRS_CONFIRM_KEYS} from '@sollapay/enums';
import {hasValue} from '@sollapay/utils';

import {formatIndividualIdentity} from './identity-document.rules';

import type {IdentityDocumentType} from '@sollapay/enums';

/**
 * Returns true when all three supplementary confirmation checkboxes are checked.
 * Shared by the entity, the policy assertion, and the frontend gating logic.
 */
export function isSupplementaryFormComplete(declarationValues: Record<string, boolean>): boolean {
  return (
    declarationValues[FATCA_CRS_CONFIRM_KEYS.BENEFICIARIES_UNCHANGED] === true &&
    declarationValues[FATCA_CRS_CONFIRM_KEYS.UPDATE_COMMITMENT] === true &&
    declarationValues[FATCA_CRS_CONFIRM_KEYS.SIGNATURE] === true
  );
}

/**
 * Whether one roster row still owes the deal type its seller-side role. Shared by
 * `TrustDetailsValidator`'s per-row readiness check and `FatcaCrsPolicy`'s whole-roster
 * assertion before signing, so "answered" means the same thing in both places.
 */
export function isSellerRoleMissing(
  seller: {sellerRole: unknown},
  asksSellerSideRole: boolean
): boolean {
  return asksSellerSideRole && !hasValue(seller.sellerRole);
}

/**
 * Appends the issuing country for a foreign-passport identity, so a rendered beneficiary
 * row is unambiguous about which country's passport it references. Takes a duck-typed
 * parameter (rather than importing `BeneficiarySnapshot`) to avoid a cycle with the entity
 * file, which already imports from this module.
 */
export function formatIdOrCompanyNumber(
  beneficiary: {
    idOrCompanyNumber: string;
    idType?: IdentityDocumentType | null;
    idCountry?: string | null;
  },
  locale = 'he'
): string {
  const {idOrCompanyNumber, idType, idCountry} = beneficiary;
  return (
    formatIndividualIdentity({idNumber: idOrCompanyNumber, idType, idCountry}, locale) ??
    idOrCompanyNumber
  );
}
